<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type {
  ParentAnalyticsSummary,
  TeacherAnalyticsSummary,
} from 'src/modules/Analytics/types';
import {
  mdiAccountMultipleOutline,
  mdiSchoolOutline,
  mdiClipboardCheckOutline,
  mdiCheckboxMarkedCircleOutline,
} from '@quasar/extras/mdi-v7';

const props = defineProps<{
  role: 'parent' | 'teacher';
  summary: ParentAnalyticsSummary | TeacherAnalyticsSummary;
}>();

const { t } = useI18n();

interface AggregateTile {
  key: string;
  acc: string;
  icon: string;
  value: number;
  label: string;
  sub: string;
}

const tiles = computed<AggregateTile[]>(() => {
  const list: AggregateTile[] = [];

  if (props.role === 'parent') {
    const s = props.summary as ParentAnalyticsSummary;
    list.push({
      key: 'children',
      acc: 'acc-courses',
      icon: mdiAccountMultipleOutline,
      value: s.children.total,
      label: 'CHILDREN_MANAGEMENT',
      sub: `${t('ACTIVE')}: ${s.children.active}`,
    });
  }

  if (props.role === 'teacher') {
    const s = props.summary as TeacherAnalyticsSummary;
    list.push(
      {
        key: 'classes',
        acc: 'acc-courses',
        icon: mdiSchoolOutline,
        value: s.classes.total,
        label: 'MY_CLASSES',
        sub: `${t('STUDENTS')}: ${s.students.total}`,
      },
      {
        key: 'assigned',
        acc: 'acc-sections',
        icon: mdiClipboardCheckOutline,
        value: s.assigned.courses,
        label: 'ASSIGNED_COURSES',
        sub: `${t('ASSIGNED_SECTIONS')}: ${s.assigned.sections}`,
      },
    );
  }

  list.push({
    key: 'completed',
    acc: 'acc-steps',
    icon: mdiCheckboxMarkedCircleOutline,
    value: props.summary.completed.steps,
    label: 'COMPLETED_STEPS',
    sub: `${t('COMPLETED_COURSES')}: ${props.summary.completed.courses}`,
  });

  return list;
});
</script>

<template>
  <div class="row q-col-gutter-md q-mb-md">
    <div
      v-for="tile in tiles"
      :key="tile.key"
      class="col-12 col-sm-6 col-md-3"
    >
      <div
        class="stat-card"
        :class="tile.acc"
      >
        <div class="stat-card__head">
          <div class="stat-card__title">{{ $t(tile.label) }}</div>
          <div class="stat-badge stat-badge--sm">
            <q-icon
              :name="tile.icon"
              size="18px"
            />
          </div>
        </div>
        <div class="col column justify-center q-mt-sm">
          <div class="stat-value">{{ tile.value }}</div>
          <div class="stat-sub q-mt-xs">{{ tile.sub }}</div>
        </div>
      </div>
    </div>
  </div>
</template>
